import { useEffect, useState } from 'react'
import { listAppSubscriptions } from '../lib/api'

// No app is granted by default anymore — mirrors backend app_catalog's
// (now-empty) DEFAULT_GRANTED / services.entitlements.org_has_app: no
// explicit AppSubscription row means no access, full stop. Child→parent
// requirements still mirror app_catalog.APP_REQUIRES.
const REQUIRES: Record<string, string> = { ping_orders: 'ping' }

type EntRow = {
  app_kind: string
  status: string
  expires_at?: string | null
  /** Pocket: when a lapsed shop's tills stop. See below for why it counts. */
  till_grace_ends_at?: string | null
}

/**
 * Reads the org's app entitlements (GET /api/v1/app-subscriptions) and exposes
 * `has(appKind)` with the same semantics as the backend gate: an app is
 * enabled only when an explicit row exists, is `active`, and has not expired.
 * Pass `{ enabled: false }` to skip the fetch on apps that don't gate anything
 * by entitlement (my).
 *
 * The expiry half was missing. `org_has_app` has always refused a row whose
 * `expires_at` is past, but this hook checked only `status`, and status stays
 * "active" through a lapse — nothing writes it. So the client and the server
 * disagreed exactly when it mattered: the launcher kept offering the app and
 * the nav kept its links, and every page behind them answered 403. Frame and
 * Pocket both, since both derive `expires_at` from their licence rows.
 */
export function useEntitlements(opts: { enabled?: boolean } = {}) {
  const enabled = opts.enabled ?? true
  const [rows, setRows] = useState<EntRow[] | null>(null)

  useEffect(() => {
    if (!enabled) return
    let alive = true
    listAppSubscriptions()
      .then(r => { if (alive) setRows(r.data as EntRow[]) })
      .catch(() => { if (alive) setRows([]) })  // on error, fall back to "nothing"
    return () => { alive = false }
  }, [enabled])

  const rowEnabled = (kind: string): boolean => {
    const row = rows?.find(r => r.app_kind === kind)
    if (row?.status !== 'active') return false
    // Null means perpetual, same as the backend reads it.
    if (!row.expires_at) return true
    if (new Date(row.expires_at).getTime() > Date.now()) return true
    // ── Lapsed, but the tills are still trading ──────────────────────────
    //
    // A Pocket subscription that has expired keeps the tills selling for a
    // grace period. Hiding the app during that window is the worst of both
    // answers: the shop is still trading, and the launcher has just removed
    // the only route to the page that says the deadline — and to the one that
    // lets somebody renew. So the app stays visible for as long as it is
    // genuinely still working.
    //
    // Only this field can widen the gate, and only the server sets it: it is
    // null for a healthy subscription, for a lapse with no expiry to measure
    // from, and for every app that is not Pocket.
    const grace = row.till_grace_ends_at
    return !!grace && new Date(grace).getTime() > Date.now()
  }

  const has = (kind: string): boolean => {
    const parent = REQUIRES[kind]
    if (parent && !has(parent)) return false
    return rowEnabled(kind)
  }

  return { has, loading: rows === null }
}
